import { Box, Modal, Typography } from '@mui/material';
import React from 'react';
import { buttonAddCategory, confirmButtonStyle, useStyles } from './styles';
import CLOSE_ICON from '../../assets/svg/close-icon.svg';
import Button from '../Button/Button';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Control, FieldValues } from 'react-hook-form';
import RemoveItemModal from '../RemoveItemModal/RemoveItemModal';
import WarningModal from '../WarningModal/WarningModal';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LANG_TEXT } from '../../constants/language';
import CategoryItem from '../CategoryItem/CategoryItem';
import CategoryRow from '../CategoryRow/CategoryRow';
import { ICategoryDetails } from '../../pages/MenuManagement/menu.types';
import { IUserMenuCategory } from '../../pages/CategoriesManagement/categoriesManagement.types';

interface ManageCategoryModalProps<TFieldValues extends FieldValues = FieldValues> {
  open: boolean;
  handleClose: () => void;
  categories: ICategoryDetails[] | IUserMenuCategory[];
  editStates: { [key: string]: { isEdit: boolean; isNew: boolean } };
  control: Control<TFieldValues>;
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  onSwitchChange: (categoryId: string, isChecked: boolean) => void;
  onEditClick: (category: ICategoryDetails | IUserMenuCategory, status: boolean) => void;
  onCheckIconClick: (categoryId: string) => void;
  onAddNewCategory: () => void;
  onMoveCategory: (dragIndex: number, hoverIndex: number) => void;
  onDeleteRequest: (categoryId: string) => void;
  onDeleteConfirm: () => void;
  deleteModalOpen: boolean;
  onDeleteModalClose: () => void;
  warningModalOpen: boolean;
  warningTitle: string;
  onWarningModalClose: () => void;
}

export function ManageCategoryModal<TFieldValues extends FieldValues = FieldValues>({
  open,
  handleClose,
  categories,
  editStates,
  control,
  onSubmit,
  onSwitchChange,
  onEditClick,
  onCheckIconClick,
  onAddNewCategory,
  onMoveCategory,
  onDeleteRequest,
  onDeleteConfirm,
  deleteModalOpen,
  onDeleteModalClose,
  warningModalOpen,
  warningTitle,
  onWarningModalClose,
}: ManageCategoryModalProps<TFieldValues>) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Modal className={classes.Modal} open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box className={classes.Box}>
        <form className={classes.Form} name="manage-category-form" noValidate autoComplete="off" onSubmit={onSubmit}>
          <Box className={classes.BoxHeader}>
            <Box className={classes.BoxTitleModal}>
              <Box className={classes.TitleModal}>
                <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-900, #101828)'}>
                  {t(LANG_TEXT.MANAGE_CATEGORY)}
                </Typography>
              </Box>
              <Box className={classes.IconClose} onClick={handleClose}>
                <img src={CLOSE_ICON} height={20} width={20} alt="CLOSE_ICON" />
              </Box>
            </Box>
          </Box>

          <DndProvider backend={HTML5Backend}>
            <Box className={classes.BoxContent}>
              {categories.map((category: ICategoryDetails | IUserMenuCategory, index: number) => (
                <CategoryItem key={category.id} category={category} index={index} moveCategory={onMoveCategory}>
                  <CategoryRow
                    category={category}
                    isEdit={editStates[category.id]?.isEdit ?? false}
                    isChecked={category.enabled}
                    control={control}
                    classes={classes}
                    onSwitchChange={onSwitchChange}
                    onEditClick={onEditClick}
                    onCheckIconClick={onCheckIconClick}
                    onDeleteClick={onDeleteRequest}
                  />
                </CategoryItem>
              ))}
            </Box>
          </DndProvider>

          <Box className={classes.BoxFooter}>
            <Button
              id="add_new_category"
              fullWidth={false}
              title={t(LANG_TEXT.ADD_NEW_CATEGORY)}
              buttonStyle={buttonAddCategory}
              sxTextStyle={{ color: 'var(--Primary-700, #296C32)', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}
              boxStyle={{
                width: 'max-content !important',
              }}
              type="button"
              onClick={onAddNewCategory}
              startIcon={<AddIcon sx={{ color: '#296C32' }} />}
            />
            <Button
              boxStyle={{
                width: 'max-content !important',
              }}
              id="done_button"
              fullWidth={false}
              title={t(LANG_TEXT.DONE)}
              buttonStyle={confirmButtonStyle}
              sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}
            />
          </Box>
          <RemoveItemModal open={deleteModalOpen} handleClose={onDeleteModalClose} handleDelete={onDeleteConfirm} />
          <WarningModal open={warningModalOpen} handleClose={onWarningModalClose} title={warningTitle} />
        </form>
      </Box>
    </Modal>
  );
}

export default ManageCategoryModal;
